import type { DeepKeys, DeepValue, Updater } from "@tanstack/react-form";
import dayjs from "dayjs";
import type { z } from "zod";

import LabeledField from "@/components/form/labeled-field";
import type { DatePickerProps } from "@/components/ui/datepicker";
import { DatePicker } from "@/components/ui/datepicker";
import type { FieldBaseProps } from "@/types/form";
import { getOnChangeFromZod } from "@/utils/form";

export type FieldDatepickerProps<
  TFormData,
  TName extends DeepKeys<TFormData> & string,
  TSchema extends z.ZodType<TFormData> | undefined,
  TData extends DeepValue<TFormData, TName> = DeepValue<TFormData, TName>,
> = FieldBaseProps<TFormData, TName, TSchema, TData> &
  Omit<DatePickerProps, "onChange">;

const FieldDatepicker = <
  TFormData,
  TName extends DeepKeys<TFormData> & string,
  TSchema extends z.ZodType<TFormData> | undefined,
  TData extends DeepValue<TFormData, TName> = DeepValue<TFormData, TName>,
>({
  Field,
  name,
  schema,
  label,
  labelProps,
  validators,
  gridRow,
  ...props
}: FieldDatepickerProps<TFormData, TName, TSchema, TData>) => (
  <Field
    name={name}
    validators={{
      onChange: schema ? getOnChangeFromZod(schema, name as string) : undefined,
      ...(validators as any), // unsolved typing issue
    }}
    children={(field) => (
      <LabeledField
        label={label}
        fieldName={field.name.toString()}
        errors={field.state.meta.errors}
        required={props.required}
        gridRow={gridRow}
        {...labelProps}
      >
        <DatePicker
          {...props}
          value={
            field.state.value && typeof field.state.value !== "boolean"
              ? dayjs(field.state.value as string | null).toDate()
              : undefined
          }
          onChange={(date) => {
            (field.handleChange as (updater: Updater<string | null>) => void)(
              date ? dayjs(date).format("YYYY-MM-DD") : null
            );
          }}
        />
      </LabeledField>
    )}
  />
);

export default FieldDatepicker;
